import { useCallback } from 'react';

import { useToasts } from '@air/provider-toast';

import { useEditCanvasStore } from '../store';
import { type AppNode, type EditUploadContentType } from '../types';
import { isAcceptedMimeType } from '../utils/uploadMimeTypes';
import { commands, useCommandHistory } from './commands';
import { useUploadAndPreload } from './useUploadAndPreload';
import { useZoomToFit } from './useZoomToFit';

export type AddImageNodeParams = {
  imageUrl: string;
  originalUrl: string;
  fileName: string;
  width: number;
  height: number;
  position?: { x: number; y: number };
  versionId?: string;
  assetId?: string;
};

async function fetchAsFile(url: string, fileName: string): Promise<{ file: File; contentType: EditUploadContentType }> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }

  const blob = await response.blob();
  const contentType: EditUploadContentType = isAcceptedMimeType(blob.type) ? blob.type : 'image/png';

  return { contentType, file: new File([blob], fileName, { type: contentType }) };
}

export const useAddImageNode = () => {
  const { execute } = useCommandHistory();
  const { uploadAndPreload } = useUploadAndPreload();
  const { showToast } = useToasts();
  const { zoomToFit } = useZoomToFit();

  const addImageNode = useCallback(
    async ({
      imageUrl,
      originalUrl,
      fileName,
      width,
      height,
      position = { x: 0, y: 0 },
      versionId,
      assetId,
    }: AddImageNodeParams): Promise<string | undefined> => {
      const previewId = crypto.randomUUID();
      const imageNodeId = crypto.randomUUID();

      if (assetId && versionId) {
        const imageNode: AppNode = {
          data: {
            assetId,
            fileName,
            media: { image: imageUrl, original: originalUrl },
            source: 'manual-edit',
            type: 'image',
            versionId,
          },
          id: imageNodeId,
          position,
          style: { height, width },
          type: 'image',
        };

        execute(commands.addNodes([imageNode]));

        return imageNode.id;
      }

      // Show an upload preview node immediately for visual feedback.
      // Added via direct setState (not command history) because preview nodes are ephemeral.
      const previewNode: AppNode = {
        data: { blobUrl: imageUrl, fileName },
        id: previewId,
        position,
        style: { height, width },
        type: 'imageUploadPreview',
      };

      // Directly add to store, bypassing command history
      useEditCanvasStore.getState().addNodes([previewNode]);

      zoomToFit({ nodeIds: [previewId] });

      try {
        const uniqueUrls = [...new Set([imageUrl, originalUrl])];
        const files = await Promise.all(uniqueUrls.map((url) => fetchAsFile(url, fileName)));
        const results = await uploadAndPreload(files);

        // Map each source URL to its CDN URL so we can resolve image/original independently.
        const cdnBySourceUrl = new Map<string, string>();
        for (const [i, url] of uniqueUrls.entries()) {
          const result = results[i];

          if (result?.status !== 'fulfilled') {
            throw new Error('Upload failed');
          }

          cdnBySourceUrl.set(url, result.cdnUrl);
        }

        const imageCdnUrl = cdnBySourceUrl.get(imageUrl);
        const originalCdnUrl = cdnBySourceUrl.get(originalUrl);

        if (!imageCdnUrl || !originalCdnUrl) {
          throw new Error('Upload failed');
        }

        const media = { image: imageCdnUrl, original: originalCdnUrl };

        const imageNode: AppNode = {
          data: {
            fileName,
            media,
            source: 'manual-edit',
            type: 'image',
          },
          id: imageNodeId,
          position,
          style: { height, width },
          type: 'image',
        };

        execute(commands.addNodes([imageNode], { removeNodeIds: [previewId] }));

        return imageNodeId;
      } catch {
        // Remove the preview node on failure.
        useEditCanvasStore.setState((state) => ({
          nodes: state.nodes.filter((n) => n.id !== previewId),
        }));
        showToast('Failed to upload image', { color: 'red' });

        return undefined;
      }
    },
    [execute, showToast, uploadAndPreload, zoomToFit],
  );

  return { addImageNode };
};
